import { useEffect, useRef, useState } from 'react';

type Options = IntersectionObserverInit & {
    once?: boolean;
    minRatio?: number;
};

const DEFAULT_THRESHOLD = 0.2;
const DEFAULT_ROOT_MARGIN = '0px 0px -10% 0px';

export function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function showing(entries: IntersectionObserverEntry[], minRatio: number | undefined): boolean {
    if (minRatio === undefined) {
        return entries.some((entry) => entry.isIntersecting);
    }
    const latest = entries.at(-1);
    return latest !== undefined && latest.isIntersecting && latest.intersectionRatio >= minRatio;
}

export function useInView<T extends Element>(options: Options = {}) {
    const { once = true, threshold = DEFAULT_THRESHOLD, rootMargin = DEFAULT_ROOT_MARGIN, root = null, minRatio } = options;
    const ref = useRef<T | null>(null);
    const [inView, setInView] = useState(() => once && prefersReducedMotion());
    const [hasBeenInView, setHasBeenInView] = useState(inView);

    useEffect(() => {
        if (once && inView) { return; }
        const el = ref.current;
        if (!el) { return; }
        const observer = new IntersectionObserver((entries) => {
            const visible = showing(entries, minRatio);
            if (once) {
                if (visible) {
                    setInView(true);
                    setHasBeenInView(true);
                    observer.disconnect();
                }
            } else {
                setInView(visible);
                if (visible) { setHasBeenInView(true); }
            }
        }, { threshold, rootMargin, root });
        observer.observe(el);
        return () => observer.disconnect();
    }, [inView, once, threshold, rootMargin, root, minRatio]);

    return [ref, inView, hasBeenInView] as const;
}
